import { createHashRouter, Navigate, RouteObject, Outlet } from 'react-router-dom'
import { ROUTES } from './config/routes'
import { ProtectedRoute } from './components/protected-route'
import { LayoutWrapper } from './components/layout/layout-wrapper'

// Auth pages
import { LoginPage } from './pages/auth/login'
import { ForgotPasswordPage } from './pages/auth/forgot-password'
import { ResetPasswordPage } from './pages/auth/reset-password'
import { CompleteInvitePage } from './pages/auth/complete-invite'
import { AuthErrorPage } from './pages/auth/auth-error'
import { TOTPVerify } from './components/auth/totp-verify'
import React from 'react'

// Admin pages
const AdminDashboard = React.lazy(() => import('./pages/admin/dashboard'))
const AdminUsers = React.lazy(() => import('./pages/admin/users'))
const AdminProfile = React.lazy(() => import('./pages/admin/profile'))
const AdminAudit = React.lazy(() => import('./pages/admin/audit'))
const AdminPartners = React.lazy(() => import('./pages/admin/partners'))
const AdminIntegrations = React.lazy(() => import('./pages/admin/integrations'))
const MerchantsPage = React.lazy(() => import('./pages/merchants'))
const MerchantDetailPage = React.lazy(() => import('./pages/merchants/merchant-detail'))
const Dashboard = React.lazy(() => import('./pages/dashboard'))
const TransactionsPage = React.lazy(() => import('./pages/transactions'))
const IntegrationsPage = React.lazy(() => import('./pages/integrations'))
const NotificationsPage = React.lazy(() => import('./pages/notifications'))
const DocumentationPage = React.lazy(() => import('./pages/documentation'))
const Users = React.lazy(() => import('./pages/users'))
const ReportPage = React.lazy(() => import('./pages/reports'))
const DailyReportPage = React.lazy(() => import('./pages/daily-reports'))
const ApiKeysPage = React.lazy(() => import('./pages/api-keys'))
const CreateApiKeyPage = React.lazy(() => import('./pages/api-keys/create'))
const EditApiKeyPage = React.lazy(() => import('./pages/api-keys/edit'))
const WebhooksPage = React.lazy(() => import('./pages/webhooks'))
const WebhookMonitoringDashboard = React.lazy(() => import('./pages/webhooks/monitoring'))
const WebhookDeliveries = React.lazy(() => import('./pages/webhooks/monitoring/deliveries'))
const WebhookDeliveryDetails = React.lazy(() => import('./pages/webhooks/monitoring/delivery-details'))
const WebhookAnalytics = React.lazy(() => import('./pages/webhooks/monitoring/analytics'))
const WebhookSpecificMonitoring = React.lazy(() => import('./pages/webhooks/monitoring/webhook-specific'))
const ProfilePage = React.lazy(() => import('./pages/profile'))

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to={ROUTES.PUBLIC.LOGIN} replace />,
  },
  {
    element: (
      <LayoutWrapper type="none">
        <Outlet />
      </LayoutWrapper>
    ),
    children: [
      {
        path: ROUTES.PUBLIC.LOGIN,
        element: <LoginPage />,
      },
      {
        path: ROUTES.PUBLIC.FORGOT_PASSWORD,
        element: <ForgotPasswordPage />,
      },
      {
        path: ROUTES.PUBLIC.RESET_PASSWORD,
        element: <ResetPasswordPage />,
      },
      {
        path: ROUTES.PUBLIC.COMPLETE_INVITE,
        element: <CompleteInvitePage />,
      },
      {
        path: ROUTES.PUBLIC.AUTH_ERROR,
        element: <AuthErrorPage />,
      },
      {
        path: '/auth/mfa-verify',
        element: <TOTPVerify />,
      },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: (
          <LayoutWrapper type="admin">
            <React.Suspense fallback={<div>Loading...</div>}>
              <Outlet />
            </React.Suspense>
          </LayoutWrapper>
        ),
        children: [
          {
            path: ROUTES.ADMIN.DASHBOARD,
            element: <AdminDashboard />,
          },
          {
            path: ROUTES.ADMIN.USERS,
            element: <AdminUsers />,
          },
          {
            path: ROUTES.ADMIN.MERCHANTS,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <MerchantsPage />
              </React.Suspense>
            ),
          },
          {
            path: '/merchants/:merchantId',
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <MerchantDetailPage />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.DASHBOARD,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <Dashboard />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.INTEGRATIONS,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <IntegrationsPage />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.NOTIFICATIONS,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <NotificationsPage />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.TRANSACTIONS,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <TransactionsPage />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.DOCUMENTATION,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <DocumentationPage />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.USERS,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <Users />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.REPORT,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <ReportPage />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.DAILY_REPORTS,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <DailyReportPage />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.API_KEYS,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <ApiKeysPage />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.API_KEYS_CREATE,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <CreateApiKeyPage />
              </React.Suspense>
            ),
          },
          {
            path: '/api-keys/edit/:apiKeyId',
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <EditApiKeyPage />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.WEBHOOKS,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <WebhooksPage />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.WEBHOOK_MONITORING,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <WebhookMonitoringDashboard />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.WEBHOOK_MONITORING_DELIVERIES,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <WebhookDeliveries />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.WEBHOOK_MONITORING_DELIVERY_DETAILS,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <WebhookDeliveryDetails />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.WEBHOOK_MONITORING_ANALYTICS,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <WebhookAnalytics />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.WEBHOOK_MONITORING_WEBHOOK_SPECIFIC,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <WebhookSpecificMonitoring />
              </React.Suspense>
            ),
          },
          {
            path: ROUTES.PARTNER.PROFILE,
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProfilePage />
              </React.Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={ROUTES.PUBLIC.LOGIN} replace />,
  },
]

export const router: ReturnType<typeof createHashRouter> = createHashRouter(routes)
